import styled, { css } from 'styled-components';

const modalTitleStyles = ({ centered, large, small }) => css`
  font-size: 20px;
  font-weight: 400;
  ${centered &&
  `
    text-align: center;
  `}
  ${large &&
  `
    font-size: 24px;
    padding-bottom: 24px;
    line-height: 1.3;
  `}
  ${small &&
  `
    font-size: 14px;
    padding-bottom: 14px;
    font-weight: bold;
  `}
`;

const modalHelpTextStyles = ({ small }) => css`
  font-size: 13px;
  line-height: 30px;
  padding-bottom: 24px;
  text-align: center;
  ${small &&
  `
    font-size: 14px;
    line-height: 20px;
  `}
`;

export const StyledToggleWrapper = styled.div`
  span {
    background-color: ${({ theme: { colors } }) => colors.gray.ultraLight};
    border: 1px solid ${({ theme: { colors } }) => colors.gray};

    &:before {
      border: 1px solid ${({ theme: { colors } }) => colors.gray};
      background-color: ${({ theme: { colors } }) => colors.gray.ultraLight};
    }
  }
`;

export const TopControlsToggleWrapper = styled(StyledToggleWrapper)`
  height: auto;
  background: ${({ theme: { colors } }) => colors.gray.extraLight};
  border: 1px solid ${({ theme: { colors } }) => colors.gray.light};
  padding: 10px 19px 10px 12px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  margin-right: 24px;
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  height: 123px;
  margin-bottom: 20px;
  padding: 8px 10px;
  font-size: 13px;
  border-color: ${({ theme: { colors } }) => colors.gray.light};
  resize: none;
`;

export const RenewalsAlertContainer = styled.div`
  margin-bottom: 16px;
`;

export const ModalContentContainer = styled.div`
  padding-bottom: 23px;
  & .edit-warning-modal__actions {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 16px;

    @media (max-width: ${({ theme: { sizes } }) => sizes.mobile}) {
      flex-direction: column-reverse;
    }
  }
`;

export const ModalTitle = styled.div`
  ${modalTitleStyles}
`;

export const ModalHelpText = styled.div`
  ${modalHelpTextStyles}
`;

export const ModalFormContainer = styled.div`
  text-align: left;

  textarea {
    height: 123px;
    resize: none;
    margin-bottom: 20px;
    border-color: ${({ theme: { colors } }) => colors.gray.light};
    padding: 8px 10px;

    &::placeholder {
      font-size: 13px;
      font-weight: 400;
      opacity: 1;
      color: ${({ theme: { colors } }) => colors.gray.medium};
    }
  }
`;

export const CheckboxLine = styled.label`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  text-align: left;
  border: 1px solid ${({ theme: { colors } }) => colors.gray.light};
  padding: 14px 19px;
  font-size: 14px;
  border-bottom-width: 0;
  user-select: none;

  &:first-child {
    border-top-right-radius: 3px;
    border-top-left-radius: 3px;
  }

  &:last-child {
    border-bottom-right-radius: 3px;
    border-bottom-left-radius: 3px;
    border-bottom-width: 1px;
  }

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  input:checked ~ i {
    background-color: ${({ theme: { colors } }) =>
      colors.brand.secondary.default};
    border-color: ${({ theme: { colors } }) => colors.brand.secondary.default};
  }

  input:checked ~ .i:after {
    display: block;
  }
`;

export const Checkmark = styled.i`
  min-height: 16px;
  min-width: 16px;
  border: 1px solid ${({ theme: { colors } }) => colors.gray.light};
  display: inline-block;
  position: relative;
  border-radius: 50%;
  margin-right: 12px;

  &:after {
    content: '';
    position: absolute;
    display: none;
  }
`;

export const AgentDashboardContainer = styled.div`
  ${({
    theme: {
      sizes: { mobile },
      colors: { white, gray, brand },
    },
  }) => css`
    padding-top: 10px;
    padding-bottom: 50px;

    & .table {
      font-size: 13px;
      margin-bottom: 20px;

      background-color: ${white};

      @media (max-width: ${mobile}) {
        background-color: inherit;
      }
    }

    & .table__head {
      display: flex;
      padding: 15px 10px;
      border: none;

      @media (max-width: ${mobile}) {
        display: none;
      }
    }

    & .table__body {
      border: none;
    }

    & .table__head-label {
      font-weight: bold;
      font-size: 14px;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    & .table__head-column {
      flex-grow: 1;
      padding: 0 5px;
      box-sizing: border-box;
      cursor: pointer;

      &--non-clickable {
        cursor: default;
      }
    }

    & .table__head-sort-arrow {
      display: inline-block;
      padding: 3px;
      margin-left: 7px;
      border: solid black;
      border-width: 0 1px 1px 0;
    }

    & .table__head-sort-arrow--asc {
      transform: rotate(45deg) translate(-2px, -2px);
    }

    & .table__head-sort-arrow--desc {
      transform: rotate(-135deg) translateX(-2px);
    }

    & .table__row {
      display: flex;
      padding: 15px 10px;
      border-top: 1px solid ${gray.light};
    }

    & .table__cell {
      flex-grow: 1;
      padding: 0 5px;
      box-sizing: border-box;
      word-break: normal;

      @media (max-width: ${mobile}) {
        padding: 0;
        margin: 20px 0 0 0;
      }
    }

    &__field {
      word-break: normal;
    }

    & .table__field-arrow {
      display: inline-block;
      padding: 3px;
      border: solid ${brand.secondary.default};
      border-width: 0 2px 2px 0;
    }

    & .table__field-arrow--top {
      transform: rotate(-135deg) translateX(-2px);
    }

    & .table__field-arrow--bottom {
      transform: rotate(45deg) translate(-2px, -2px);
    }

    & .checkbox-group__input--squared {
      margin-right: 7px;
    }

    & .checkbox-group__input--squared input[type='checkbox'] {
      width: 25px;
      height: 25px;
    }

    & .table__head-column--account-name,
    & .table__cell--account-name {
      width: 16%;
    }

    & .table__head-column--contact,
    & .table__cell--contact {
      width: 20%;
    }

    & .table__head-column--last-activity,
    & .table__head-column--effective-date,
    & .table__cell--last-activity {
      width: 14%;
    }

    & .table__cell--last-activity,
    & .table__cell--insurance-type,
    & .table__cell--insurance-type,
    & .table__cell--contact,
    & .table__cell--premium,
    & .table__cell--status {
      margin-bottom: -3px;
    }

    & .table__cell--last-activity div,
    & .table__cell--insurance-type div,
    & .table__cell--insurance-type div,
    & .table__cell--contact div,
    & .table__cell--premium div,
    & .table__cell--status div {
      margin-bottom: 3px;

      .multiline {
        display: block;
        min-height: 40px;
      }
    }

    & .table__cell--last-activity .multiline,
    & .table__cell--insurance-type .multilinev,
    & .table__cell--insurance-type .multilinev,
    & .table__cell--contact .multilinev,
    & .table__cell--premium .multilinev,
    & .table__cell--status .multilinev {
      display: block;
      min-height: 40px;
    }

    & .table__head-column--insurance-type,
    & .table__cell--insurance-type {
      width: 13%;
    }

    & .table__head-column--industry,
    & .table__cell--industry {
      width: 16%;
    }

    & .table__head-column--status,
    & .table__cell--status {
      width: 15%;
    }

    & .table__head-column--actions,
    & .table__cell--actions {
      width: 9%;
      margin-right: 0;
    }

    & .table__head-column--premium,
    & .table__cell--premium {
      width: 8%;
    }

    & .table__head-column--actions-account,
    & .table__cell--actions-account {
      width: 11%;
      margin-right: 0;
    }

    & .table__cell--actions {
      cursor: pointer;
    }

    .table--prospects-with-scoring {
      & .table--prospects-with-scoring .table__head-column--account-name,
      & .table--prospects-with-scoring .table__cell--account-name {
        width: 21%;
      }

      & .table--prospects-with-scoring .table__head-column--contact,
      & .table--prospects-with-scoring .table__cell--contact {
        width: 17%;
      }

      & .table--prospects-with-scoring .table__head-column--industry,
      & .table--prospects-with-scoring .table__cell--industry {
        width: 15%;
      }

      & .table--prospects-with-scoring .table__cell--fulltime-employees,
      & .table--prospects-with-scoring .table__head-column--fulltime-employees {
        width: 17%;
      }

      & .table--prospects-with-scoring .table__cell--scoring,
      & .table--prospects-with-scoring .table__head-column--scoring {
        width: 17%;
      }
    }
  `}
`;

export const AgentDashboardTopControls = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const AgentDashboardCtaContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${({ theme: { sizes } }) => sizes.mobile}) {
    position: fixed;
    bottom: 0;
    z-index: 1;
    display: block;

    width: 100%;
    padding: 15px;
    margin: -15px;

    background: ${({ theme: { colors } }) => colors.white};
  }
`;

export const AgentDashboardSearchWrapper = styled.div`
  position: relative;
  display: flex;
  margin-left: 2px;

  @media (max-width: ${({ theme: { sizes } }) => sizes.mobile}) {
    width: 100%;
    svg {
      top: 20px;
    }
  }
`;

export const AgentDashboardSearch = styled.input`
  padding: 8px 40px 6px 10px;
  font-size: 14px;
  background-color: ${({ theme: { colors } }) => colors.white};
  border: solid 1px ${({ theme: { colors } }) => colors.gray.dark};

  @media (max-width: ${({ theme: { sizes } }) => sizes.mobile}) {
    width: 100%;
    padding: 10px;
  }
`;
